import * as React from 'react';
import { createPortal } from 'react-dom';
import { css, StyleSheet } from 'aphrodite';
import { Colors, MediaBreakPointUp } from '@lean-body/components/styles';
import { ArrowDirection, ArrowIcon } from '../../atoms/icons/arrow_icon';
import { SearchIcon } from '../../atoms/icons/search_icon';
import { SearchFilterChips } from './search_filter_chips';
import { SearchFilterDialog } from './search_filter_dialog';
import { hasAnyFilter, SEARCH_FILTERS, selectedFilterCount, } from './search_filter_state';
export const SearchResultHeader = ({ config, keyword, lessonsCount, sortLabel, filters, styles: lessonStyles, durations, difficulties, partTags, trainers, onBack, onSubmitKeyword, onClearKeyword, onClickSort, onApplyFilters, }) => {
    const [addition, setAddition] = React.useState('');
    const additionInputRef = React.useRef(null);
    const [openFilter, setOpenFilter] = React.useState(null);
    const [draft, setDraft] = React.useState(filters);
    const resultCountLabel = lessonsCount === undefined ? '' : `検索結果：${lessonsCount}件`;
    const keywordTokens = keyword.trim().split(/\s+/).filter(Boolean);
    const filterChips = SEARCH_FILTERS.map(({ key, label }) => ({
        key,
        label,
        active: selectedFilterCount(filters, key) > 0,
    }));
    const open = (key) => {
        setDraft(filters);
        setOpenFilter(key);
    };
    const toggle = (key, id) => {
        const next = Object.assign({}, draft);
        const flip = (ids) => (ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]);
        if (key === 'style')
            next.styleIDs = flip(draft.styleIDs);
        if (key === 'duration')
            next.durationIDs = flip(draft.durationIDs);
        if (key === 'difficulty')
            next.difficultyIDs = flip(draft.difficultyIDs);
        if (key === 'tag')
            next.tagIDs = flip(draft.tagIDs);
        if (key === 'trainer')
            next.trainerIDs = flip(draft.trainerIDs);
        setDraft(next);
    };
    const selectCategory = (category) => {
        setDraft(Object.assign(Object.assign({}, draft), { category }));
    };
    const submitKeyword = (e) => {
        e.preventDefault();
        const extra = addition.trim();
        if (!extra)
            return;
        setAddition('');
        onSubmitKeyword([...keywordTokens, extra].join(' '));
    };
    const removeKeywordToken = (index, event) => {
        event.stopPropagation();
        const next = keywordTokens.filter((_, tokenIndex) => tokenIndex !== index).join(' ');
        if (next)
            onSubmitKeyword(next);
        else
            onClearKeyword();
    };
    const applyFilters = () => {
        onApplyFilters(draft);
        setOpenFilter(null);
    };
    return (React.createElement("div", { className: css(styles.wrap) },
        React.createElement("div", { className: css(styles.searchRow) },
            React.createElement("div", { className: css(styles.back), onClick: onBack, "aria-label": "\u623B\u308B" },
                React.createElement(ArrowIcon, { direction: ArrowDirection.left, color: Colors.Black })),
            React.createElement("form", { className: css(styles.form), onSubmit: submitKeyword, onClick: () => { var _a; return (_a = additionInputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } },
                React.createElement(SearchIcon, { color: Colors.GrayDarkest, classObject: styles.searchIcon }),
                keywordTokens.map((token, index) => (React.createElement("div", { key: `${token}-${index}`, className: css(styles.keywordChip) },
                    token,
                    React.createElement("span", { className: css(styles.keywordClear), onClick: (event) => removeKeywordToken(index, event) }, "\u00D7")))),
                React.createElement("input", { ref: additionInputRef, className: css(styles.input), value: addition, placeholder: keywordTokens.length ? '' : 'レッスンを検索する', onChange: (e) => setAddition(e.target.value) }))),
        React.createElement(SearchFilterChips, { chips: filterChips, onClick: open }),
        React.createElement("div", { className: css(styles.metaRow) },
            React.createElement("div", null, resultCountLabel),
            React.createElement("div", { className: css(styles.sort), onClick: onClickSort },
                sortLabel,
                React.createElement(ArrowIcon, { direction: ArrowDirection.bottom, color: Colors.Black, classObject: styles.chevron }))),
        openFilter &&
            createPortal(React.createElement(SearchFilterDialog, { config: config, active: openFilter, setActive: setOpenFilter, draft: draft, styles: lessonStyles, durations: durations, difficulties: difficulties, partTags: partTags, trainers: trainers, onToggle: toggle, onSelectCategory: selectCategory, onClose: () => setOpenFilter(null), onSubmit: applyFilters, submitDisabled: !hasAnyFilter(draft) }), document.body)));
};
const styles = StyleSheet.create({
    wrap: {
        maxWidth: 1024,
        margin: '0 auto',
        padding: '16px 16px 16px',
        [MediaBreakPointUp.MD]: {
            padding: '38px 23px 16px',
        },
        [MediaBreakPointUp.XL]: {
            padding: '38px 32px 16px',
        },
    },
    searchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    back: {
        display: 'flex',
        cursor: 'pointer',
    },
    form: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        minHeight: 40,
        border: `1px solid ${Colors.PrimaryDark}`,
        borderRadius: 20,
        padding: '4px 16px',
    },
    searchIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
    },
    keywordChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 30,
        background: Colors.BackgroundLightGray,
        borderRadius: 15,
        padding: '0 10px',
        fontSize: 14,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
    },
    keywordClear: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: Colors.GrayDarkest,
        fontSize: 16,
        lineHeight: '16px',
    },
    input: {
        flex: 1,
        minWidth: 80,
        border: 'none',
        outline: 'none',
        fontSize: 14,
        background: 'transparent',
    },
    metaRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 16,
        fontSize: 13,
    },
    sort: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer',
        fontSize: 13,
    },
    chevron: {
        width: 12,
        height: 12,
    },
});
